import type { KnowledgeSource } from '@agentic/contracts';
import {
  Badge,
  Button,
  cn,
  Dialog,
  EmptyState,
  Field,
  formatDateTime,
  formatRelative,
  IdText,
  Input,
  KeyValue,
  lifecycleKind,
  Select,
  SkeletonRows,
  statusTone,
  Table,
  useToast,
} from '@agentic/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Search, Upload } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { client } from '../../app/api.ts';
import { DisclosureSummary } from '../../app/disclosure.tsx';
import { keys, sourcesQuery } from '../../app/queries.ts';
import { isOperator } from '../../app/roles.ts';
import { ErrorBanner, QueryError } from '../../app/route-states.tsx';
import { CARD, PAGE, PageHeader } from '../../app/shell.tsx';
import { useMe } from '../../app/use-me.ts';
import {
  accessSummary,
  sourceStatusLabel,
  verificationStatus,
} from '../../features/knowledge/source-model.ts';
import { UploadDrawer } from '../../features/knowledge/upload-drawer.tsx';
import { EvidenceTab } from '../../features/task-detail/blocks/detail-tabs.tsx';

export const Route = createFileRoute('/_app/knowledge')({ component: KnowledgePage });

function KnowledgePage() {
  const me = useMe();
  const q = useQuery(sourcesQuery);
  const [upload, setUpload] = useState(false);
  const operator = isOperator(me);
  const scopes = Array.from(new Set(q.data?.items.map((s) => s.scope) ?? [])).sort();
  const groups = Array.from(
    new Set([...me.aclSubjects, ...(q.data?.items.flatMap((s) => s.aclSubjects) ?? [])]),
  ).sort();
  return (
    <>
      <PageHeader
        title="Knowledge"
        description={
          <>
            <p className="wb-prose text-ink-2">
              Documents agents can cite as evidence. You see the sources your access groups can use. Text
              taken from a source is treated as data, never as instructions.
            </p>
            <details className="group mt-1">
              <DisclosureSummary>Your access groups</DisclosureSummary>
              <p className="mt-1 text-[13px] text-ink-2">
                {me.aclSubjects.length === 0 ? (
                  'You are in no access groups, so no sources are visible.'
                ) : (
                  <>
                    {accessSummary(me.aclSubjects)}. Exact ids:{' '}
                    {me.aclSubjects.map((a, i) => (
                      <span key={a}>
                        {i > 0 && ', '}
                        <code className="font-mono text-[12.5px] text-ink">{a}</code>
                      </span>
                    ))}
                  </>
                )}
              </p>
            </details>
          </>
        }
        actions={
          operator ? (
            <Button
              variant="primary"
              icon={<Upload className="size-4" aria-hidden />}
              onClick={() => setUpload(true)}
            >
              Upload source
            </Button>
          ) : undefined
        }
      />
      <div className={PAGE}>
        <div className={`overflow-hidden ${CARD}`}>
          {q.isPending ? (
            <SkeletonRows label="Loading sources" />
          ) : q.isError ? (
            <div className="p-4">
              <QueryError error={q.error} onRetry={() => void q.refetch()} />
            </div>
          ) : q.data.items.length === 0 ? (
            <EmptyState title="No sources visible to you">
              You only see sources that one of your access groups can use. Ask an operator to share a source
              with your group.
            </EmptyState>
          ) : (
            <Table aria-label="Knowledge sources">
              <thead>
                <tr>
                  <th scope="col">Source</th>
                  <th scope="col">Who can use this source?</th>
                  <th scope="col">Classification</th>
                  <th scope="col">Status</th>
                  <th scope="col">Verification</th>
                  {operator && (
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((s) => (
                  <SourceRow key={s.id} source={s} operator={operator} />
                ))}
              </tbody>
            </Table>
          )}
        </div>
        {operator && <SearchBox scopes={scopes} />}
      </div>
      {operator && <UploadDrawer open={upload} onOpenChange={setUpload} scopes={scopes} groups={groups} />}
    </>
  );
}

const VERIFY_TONE = { never: 'warn', overdue: 'warn', verified: 'ok' } as const;

function SourceRow({ source: s, operator }: { source: KnowledgeSource; operator: boolean }) {
  const verification = verificationStatus(s);
  return (
    <tr data-source-id={s.id}>
      <td className="min-w-[14rem] align-top">
        <div className="font-medium text-ink">{s.name}</div>
        <div className="text-[13px] text-ink-2">
          Version {s.version} · collection {s.scope}
        </div>
        <details className="group">
          <DisclosureSummary>Source details</DisclosureSummary>
          <KeyValue
            className="mt-1 mb-2"
            items={[
              { label: 'Source id', value: <IdText value={s.id} copy label="source id" /> },
              { label: 'Scope', value: <code className="font-mono text-[12.5px]">{s.scope}</code> },
              { label: 'Kind', value: s.kind },
              { label: 'Indexed chunks', value: s.chunkCount },
              { label: 'Owner', value: <IdText value={s.ownerSubject} /> },
              {
                label: 'Content hash',
                value: <IdText value={s.hash} length={16} copy label="content hash" />,
              },
              { label: 'Status (raw)', value: <code className="font-mono text-[12.5px]">{s.status}</code> },
              { label: 'Created', value: formatDateTime(s.createdAt) },
              { label: 'Updated', value: formatDateTime(s.updatedAt) },
            ]}
          />
        </details>
      </td>
      <td className="min-w-[12rem] align-top">
        <div className="text-ink">{accessSummary(s.aclSubjects)}</div>
        <details className="group">
          <DisclosureSummary>Exact access list</DisclosureSummary>
          <ul
            className="mt-1 mb-2 flex max-w-[16rem] flex-wrap gap-1"
            aria-label={`Access list for ${s.name}`}
          >
            {s.aclSubjects.map((a) => (
              <li key={a}>
                <Badge mono className="h-auto! min-h-5.5 max-w-full whitespace-normal! break-all py-0.5">
                  {a}
                </Badge>
              </li>
            ))}
          </ul>
        </details>
      </td>
      <td className="align-top">
        <Badge
          tone={s.classification === 'confidential' || s.classification === 'restricted' ? 'warn' : 'neutral'}
        >
          {s.classification}
        </Badge>
      </td>
      <td className="align-top">
        <Badge
          tone={statusTone(lifecycleKind(s.status))}
          dot
          pulse={s.status === 'ingesting'}
          title={s.status}
          data-status={s.status}
        >
          {sourceStatusLabel(s.status)}
          <span className="sr-only"> ({s.status})</span>
        </Badge>
      </td>
      <td className="min-w-[11rem] align-top">
        <Badge tone={VERIFY_TONE[verification.kind]} dot data-verification={verification.kind}>
          {verification.label}
        </Badge>
        <div className="mt-1 text-[13px] text-ink-2">
          {s.lastVerifiedAt ? (
            <>
              Last verified{' '}
              <span title={formatDateTime(s.lastVerifiedAt)}>{formatRelative(s.lastVerifiedAt)}</span>
              <span className="block text-[12px] text-ink-3">{formatDateTime(s.lastVerifiedAt)}</span>
            </>
          ) : (
            verification.explanation
          )}
        </div>
      </td>
      {operator && (
        <td className="text-right align-top">
          <DeleteSource source={s} />
        </td>
      )}
    </tr>
  );
}

function DeleteSource({ source }: { source: KnowledgeSource }) {
  const { id, name } = source;
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const del = useMutation({
    mutationFn: () => client.knowledge.delete({ params: { sourceId: id } }),
    onSuccess: async () => {
      toast.push({ title: `Deleted ${name}`, tone: 'neutral' });
      setConfirm(false);
      await qc.invalidateQueries({ queryKey: keys.sources });
    },
  });
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setConfirm(true)} aria-label={`Delete ${name}`}>
        Delete
      </Button>
      <Dialog
        open={confirm}
        onOpenChange={(o) => {
          if (!del.isPending) setConfirm(o);
        }}
        title={`Delete ${name}?`}
        description="Agents can no longer retrieve or cite this source in new work."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(false)} disabled={del.isPending}>
              Keep
            </Button>
            <Button variant="danger" loading={del.isPending} onClick={() => del.mutate()}>
              Confirm delete
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 text-left text-[14px] text-ink-2">
          <p>
            Removes version {source.version} of <span className="font-medium text-ink">{name}</span> (
            {source.chunkCount} indexed {source.chunkCount === 1 ? 'chunk' : 'chunks'}) for everyone in{' '}
            {accessSummary(source.aclSubjects)}.
          </p>
          <p>Effect on citations in existing tasks: not verified by this console.</p>
          {del.isError && <ErrorBanner error={del.error} title="The source was not deleted" showMessage />}
        </div>
      </Dialog>
    </>
  );
}

const SearchForm = z.object({ query: z.string().trim().min(1, 'Enter a query'), scope: z.string() });

function SearchBox({ scopes }: { scopes: string[] }) {
  const [open, setOpen] = useState(false);
  const form = useForm<z.infer<typeof SearchForm>>({
    resolver: zodResolver(SearchForm),
    defaultValues: { query: '', scope: '' },
  });
  const search = useMutation({
    mutationFn: (v: z.infer<typeof SearchForm>) =>
      client.knowledge.search({ body: { query: v.query, scopes: v.scope ? [v.scope] : [], topK: 8 } }),
  });
  const error = form.formState.errors.query?.message;
  return (
    <section aria-labelledby="debug-search-h" className={CARD}>
      <header className="flex flex-wrap items-start justify-between gap-3 px-6 py-5">
        <div className="min-w-0 max-w-2xl">
          <div className="text-[13px] font-medium text-ink-3">Operator tools</div>
          <h2 id="debug-search-h" className="text-[20px] leading-snug font-semibold tracking-[-0.01em]">
            Debug search
          </h2>
          <p className="wb-prose mt-0.5 text-ink-2">
            Runs the same access-filtered retrieval an agent would run as you. Use it to diagnose a missing or
            unexpected citation; it does not change any source.
          </p>
        </div>
        <Button
          size="sm"
          aria-expanded={open}
          {...(open ? { 'aria-controls': 'debug-search-body' } : {})}
          icon={
            <ChevronDown aria-hidden className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
          }
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Hide debug search' : 'Show debug search'}
        </Button>
      </header>
      {open && (
        <div id="debug-search-body" className="border-t border-line px-6 pt-5 pb-6">
          <form
            aria-label="Knowledge search"
            className="flex flex-wrap items-end gap-3"
            onSubmit={form.handleSubmit((v) => search.mutate(v))}
            noValidate
          >
            <Field id="kq" label="Query" error={error} className="w-full min-w-0 sm:min-w-[16rem] sm:flex-1">
              <Input id="kq" {...form.register('query')} />
            </Field>
            <Field id="ks" label="Scope" className="w-full sm:w-56">
              <Select id="ks" {...form.register('scope')}>
                <option value="">All scopes</option>
                {scopes.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </Select>
            </Field>
            <Button type="submit" icon={<Search className="size-4" aria-hidden />} loading={search.isPending}>
              Search
            </Button>
          </form>
          {search.isError && (
            <ErrorBanner className="mt-4" error={search.error} title="The search did not run" />
          )}
          <div className="mt-4" aria-live="polite">
            {search.data && (
              <>
                <div className="mb-2 text-[13px] text-ink-3">
                  {search.data.items.length} {search.data.items.length === 1 ? 'result' : 'results'}
                </div>
                <EvidenceTab evidence={search.data.items} />
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
